import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField,
  Button, IconButton, useTheme, useMediaQuery, Chip
} from '@mui/material';
import { Close as CloseIcon, Phone as PhoneIcon, Person as PersonIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useUser } from '../../context/UserContext';
import { invokeApi, apiList } from '../../services/apiServices';
import { config } from '../../config/config';
import popupImg from '../../assets/ar-popup-services.png';

const LeadPopup = () => {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [dismissedPages, setDismissedPages] = useState(() => new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  useEffect(() => {
    // 0. For logged-in users, NEVER show popup
    if (user || localStorage.getItem('ar_user') || localStorage.getItem('amrutha_user')) {
      setOpen(false);
      return;
    }

    const pathname = (location?.pathname || window.location.pathname || '').toLowerCase();

    // 1. NEVER show on login, register, dashboard, admin, or invoice pages
    if (
      pathname.includes('/login') ||
      pathname.includes('/register') ||
      pathname.includes('/dashboard') ||
      pathname.includes('/admin') ||
      pathname.includes('/invoice')
    ) {
      setOpen(false);
      return;
    }

    // 2. Once submitted form, close for the entire application permanently
    const hasSubmitted =
      sessionStorage.getItem('ar_lead_submitted') === 'true' ||
      localStorage.getItem('ar_lead_submitted') === 'true' ||
      Cookies.get('ar_lead_interacted') === 'submitted' ||
      Cookies.get('amrutha_lead_interacted');

    if (hasSubmitted) {
      setOpen(false);
      return;
    }

    // 3. If customer clicked cancel on THIS particular page during this browse session, don't show on this page
    if (dismissedPages.has(pathname)) {
      setOpen(false);
      return;
    }

    // 4. Start timer (shows on refresh if customer hasn't submitted details)
    const timer = setTimeout(() => {
      const currentPath = (window.location.pathname || '').toLowerCase();
      if (
        !currentPath.includes('/login') &&
        !currentPath.includes('/register') &&
        !currentPath.includes('/dashboard') &&
        !currentPath.includes('/admin') &&
        !currentPath.includes('/invoice') &&
        !dismissedPages.has(currentPath)
      ) {
        setOpen(true);
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [location.pathname, dismissedPages]);

  const handleClose = () => {
    setOpen(false);
    // Cancel button won't show to that particular page during current visit
    const pathname = (location?.pathname || window.location.pathname || '').toLowerCase();
    setDismissedPages(prev => new Set(prev).add(pathname));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    if (cleanPhone.length < 10) {
      setPhoneError('Please enter a valid 10-digit mobile number');
      return;
    }
    setPhoneError('');
    setLoading(true);
    try {
      const response = await invokeApi(config.apiUrl + apiList.addLead, {
        name: name.trim(),
        phone: cleanPhone,
        personType: 'Website Visitor'
      });
      if (response?.status === 200 || response?.data?.responseCode === '200') {
        // Form submitted: permanently close for application
        try {
          sessionStorage.setItem('ar_lead_submitted', 'true');
          localStorage.setItem('ar_lead_submitted', 'true');
          Cookies.set('ar_lead_interacted', 'submitted', { expires: 365, path: '/' });
        } catch (e) {}
        setOpen(false);
        alert('Thank you! Our expert will contact you shortly.');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Do not render popup under any circumstances for logged-in users
  if (user || localStorage.getItem('ar_user') || localStorage.getItem('amrutha_user')) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: 'transparent',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          m: { xs: 1, sm: 2 },
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: { xs: 'auto', sm: 500 } }}>

          {/* Left Column — Full Image (55% width) */}
          <Box
            sx={{
              width: { xs: '100%', sm: '55%' },
              minHeight: { xs: 220, sm: 'auto' },
              position: 'relative',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {/* Blurred background image */}
            <Box
              component="img"
              src={popupImg}
              alt="AR Infotech Services"
              sx={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            {/* Dark overlay gradient */}
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                background: 'linear-gradient(135deg, rgba(10,22,60,0.55) 0%, rgba(14,58,122,0.4) 100%)',
              }}
            />
            {/* Overlay text */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: { xs: 3, sm: 4 },
              }}
            >

            </Box>
          </Box>

          {/* Right Column — Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              background: '#FFFFFF',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 10,
                top: 10,
                zIndex: 10,
                backgroundColor: 'rgba(0,0,0,0.06)',
                color: '#555',
                '&:hover': { backgroundColor: '#fee', color: '#E53935' },
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem' }} />
            </IconButton>

            <Chip
              label="🎯 Special Offer"
              size="small"
              sx={{
                mb: 2, alignSelf: 'flex-start',
                backgroundColor: 'rgba(14,106,218,0.1)',
                color: '#0e6ada',
                fontWeight: 700,
                fontSize: '0.75rem'
              }}
            />

            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: '#0e3a7a', lineHeight: 1.3 }}>
              Get Genuine Microsoft Licenses
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Leave your details and get expert help choosing the right Windows, Office, or Server license at the best price.
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Your Name"
                variant="outlined"
                margin="dense"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                size="small"
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: '#0e6ada', mr: 1, fontSize: '1.2rem' }} />
                }}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                margin="dense"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (phoneError) setPhoneError('');
                }}
                error={Boolean(phoneError)}
                helperText={phoneError}
                autoComplete="tel"
                size="small"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ color: '#0e6ada', mr: 1, fontSize: '1.2rem' }} />
                }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                  boxShadow: '0 6px 20px rgba(14,106,218,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0b54b0 0%, #0080aa 100%)',
                    boxShadow: '0 8px 24px rgba(14,106,218,0.45)',
                  }
                }}
              >
                {loading ? 'Submitting...' : 'Get Expert Advice →'}
              </Button>
            </form>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center', display: 'block' }}>
              🔒 Your information is safe and never shared.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LeadPopup;
